// src/views/MenageAvisView.jsx — LA PAGE AVIS (maquette validée Dimitri
// 2026-08-21) — lecture seule, agent (`menage`) et orchestrateur.
//
// LA RÈGLE DE FOND : sur les plateformes, tout ce qui n'est pas 5 étoiles
// compte CONTRE nous. Le seuil est 5, pas 4 — « sous 5★ » = rating < 5.
//
//   · BANDEAU navy pleine largeur : « Avis » + « 61 avis · 3 logements », et
//     TROIS blocs (note générale · propreté · avis sous 5★ ce mois).
//   · DEUX BLOCS CÔTE À CÔTE (≥ 900 px ; empilés dessous) :
//       – PAR LOGEMENT : deux pastilles côte à côte (GÉNÉRAL / MÉNAGE) ; un
//         ménage SOUS la générale ambre la carte et LE DIT (« Le ménage tire
//         la note vers le bas. ») ;
//       – LES MOINS BONNES NOTES du mois (flèches, jamais mémorisé) : tous
//         logements mélangés, chaque carte NOMME le sien, commentaire déplié
//         d'office — un 3,8 général avec 5 en ménage se LIT (le problème
//         vient d'ailleurs, pas de l'équipe).
//   · TOUS LES AVIS en bas, REPLIÉS par défaut (la page faisait 12 000 px),
//     filtre par logement, deux notes seulement — plus de chips de catégories.
//   · RÉPONDRE AUX AVIS = Dimitri seul. AUCUN mécanisme de réponse ici.
//
// CE QUE LA VUE NE MONTRE JAMAIS (l'endpoint menage_avis ne les sert pas) :
// aucun NOM de voyageur (ADR-205), aucun MONTANT, aucune modération. Texte
// voyageur ORIGINAL en lecture seule (DGCCRF, art. L.111-7-2 C. conso).
// UN appel serveur (menage_avis) — tout le reste se calcule au rendu.

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Star, Sparkles, Frown, RefreshCw, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react'
import { DS } from '../lib/ui'
import CarteTitrePage, { boutonCarteTitre } from '../components/CarteTitrePage'
import { fetchMenageAvis } from '../lib/menage-avis-api'

const AMBRE = '#d4a24c'

// « 21 août » — LE format de date (règle du dessin unique) : jamais de jour
// de semaine, jamais d'année.
function dateCourteFR(iso) {
  const t = Date.parse(`${String(iso || '').slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(t)) return ''
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' }).format(t)
}
// « 4,81 » — deux décimales, virgule. null/undefined → « — » (Number(null)
// vaut 0 : sans la garde, une absence deviendrait un faux « 0,00 »).
function fmtFR2(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—'
  return Number(n).toFixed(2).replace('.', ',')
}
function libelleMoisFR(month) {
  const t = Date.parse(`${month}-01T12:00:00Z`)
  if (Number.isNaN(t)) return month
  return new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'Europe/Paris' }).format(t)
}
function moisVoisin(month, pas) {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + pas, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}
function moisCourantParis() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date()).slice(0, 7)
}

// Lookup logement_id → libellé lisible (logs + chambres sous-loc). Jamais un
// id technique en titre.
function buildLogLabel(cfg) {
  const byId = {}
  for (const prop of cfg?.props || []) {
    for (const log of prop.logs || []) {
      if (log?.id) byId[log.id] = log.nom_complet || log.nom || log.id
      for (const ch of log.chambres || []) {
        if (ch?.id) byId[ch.id] = ch.nom_complet || ch.nom || ch.id
      }
    }
  }
  return (logementId) => byId[logementId] || 'Logement'
}

function noteMenage(avis) {
  const v = Number(avis?.category_ratings?.cleanliness)
  return Number.isFinite(v) ? v : null
}
// A-c (tri Dimitri 2026-08-21) : rating null ≠ 0. Number(null) === 0 — sans
// cette garde, un avis SANS note serait compté « sous 5★ » et tirerait les
// moyennes vers le bas. Même garde explicite que le serveur (menage-avis-
// handlers.aggregateAvisStats).
function noteGenerale(avis) {
  if (avis?.rating == null) return null
  const v = Number(avis.rating)
  return Number.isFinite(v) ? v : null
}

// LES PALIERS DE LA NOTE DE MÉNAGE (règle Dimitri 2026-08-22) :
//   sous 4 → ROUGE #b8412b · de 4 à moins de 5 → AMBRE #d4a24c ·
//   exactement 5 → VERT #7fc4a3. Donc 4,00 est AMBRE : tout ce qui n'est
//   pas 5 se voit. La note GÉNÉRALE, elle, reste BLEUE #5c8ab8 quelle que
//   soit sa valeur — elle donne le contexte, elle ne nous juge pas.
// ⚠️ LE TEXTE EST FONCÉ, LE PLEIN RESTE CLAIR (Dimitri 2026-08-23). Le vert
// #7fc4a3 se lisait mal en TEXTE sur fond clair — il est fait pour une pastille
// PLEINE avec du blanc dessus. Chaque palier a donc deux couleurs distinctes :
//   · `fg` + `bg` + `bord` → texte coloré sur fond clair (lisible) ;
//   · `plein` → aplat de couleur, texte blanc par-dessus.
// Les trois paliers sont traités pareil : l'ambre et le rouge étaient pâles
// pour la même raison.
export function palierMenage(n) {
  if (n == null || Number.isNaN(Number(n))) {
    return { fg: '#5c6773', bg: '#f1f3f5', bord: '#dfe3e8', plein: '#98a2ad' }
  }
  if (n < 4) return { fg: '#8f2f1d', bg: '#fdf4f0', bord: '#e0b5a5', plein: '#b8412b' }
  if (n < 5) return { fg: '#8a5f19', bg: '#fdf7ee', bord: '#e8d4b0', plein: '#d4a24c' }
  return { fg: '#124a33', bg: '#f0f7f3', bord: '#c9e0d4', plein: '#7fc4a3' }
}
const BLEU_GENERALE = '#5c8ab8'

// Pastille pleine de note générale — TOUJOURS BLEUE (règle 22/08) : elle
// donne le contexte, quelle que soit sa valeur.
function PastilleNote({ n, testId }) {
  return (
    <span data-testid={testId} style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
      background: BLEU_GENERALE, color: '#fff', borderRadius: 7,
      padding: '3px 9px', fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
      <Star size={11} strokeWidth={2.4} fill="#fff" /> {fmtFR2(n)}
    </span>
  )
}
// Pastille ménage : LES TROIS PALIERS (règle 22/08) — <4 rouge, 4–<5 ambre,
// 5 vert, absente grise. C'est ELLE qui porte la couleur qui nous juge.
function PastilleMenage({ n, testId }) {
  const absente = n == null
  const p = palierMenage(n)
  return (
    <span data-testid={testId} data-basse={!absente && n < 5 ? '1' : '0'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
        background: p.bg, color: p.fg, border: `1px solid ${p.bord}`,
        borderRadius: 7, padding: '3px 9px', fontSize: 12, fontWeight: 700,
        fontVariantNumeric: 'tabular-nums' }}>
      <Sparkles size={11} strokeWidth={2.4} /> {absente ? '—' : fmtFR2(n)}
    </span>
  )
}

export default function MenageAvisView({ cfg, currentUser }) {
  const [payload, setPayload] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Le mois du bloc de droite — JAMAIS mémorisé, ouvre sur le courant.
  const moisCourant = moisCourantParis()
  const [mois, setMois] = useState(moisCourant)
  // Le filtre de la liste du bas : 'tous' | logement_id. Jamais mémorisé.
  const [filtreLog, setFiltreLog] = useState('tous')
  const [deplies, setDeplies] = useState(() => new Set())
  const [estLarge, setEstLarge] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(min-width: 900px)').matches : true)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const mq = window.matchMedia('(min-width: 900px)')
    const maj = () => setEstLarge(mq.matches)
    mq.addEventListener('change', maj)
    return () => mq.removeEventListener('change', maj)
  }, [])

  const charger = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      setPayload(await fetchMenageAvis())
    } catch (e) {
      setError(e?.message || 'Chargement échoué')
    } finally { setLoading(false) }
  }, [])
  useEffect(() => { charger() }, [charger])

  const labelFor = useMemo(() => buildLogLabel(cfg), [cfg])
  const avis = useMemo(() => (Array.isArray(payload?.avis) ? payload.avis : []), [payload])

  // ── TOUT SE CALCULE AU RENDU — un seul appel serveur. ──
  // A-a (tri Dimitri 2026-08-21) : AVANT l'arrivée des données, le bandeau
  // affichait « 0 avis · 0 logement » — un mensonge. Tirets tant que rien
  // n'est chargé ; les vrais chiffres seulement quand ils existent.
  const pret = payload != null
  const calc = useMemo(() => {
    const nbLogements = new Set(avis.map((a) => a.logement_id)).size
    const notes = avis.map(noteGenerale).filter((v) => v != null)
    const menages = avis.map(noteMenage).filter((v) => v != null)
    const avg = (xs) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null)
    // LE SEUIL EST 5 : strictement sous 5 = mauvais avis.
    const sous5MoisCourant = avis.filter((a) => {
      const n = noteGenerale(a)
      return n != null && n < 5 && String(a.review_date || '').slice(0, 7) === moisCourant
    }).length
    // Par logement — ordre de première apparition (serveur trie date desc).
    const parLogement = []
    const idx = new Map()
    for (const a of avis) {
      const lid = a.logement_id || '—'
      let g = idx.get(lid)
      if (!g) { g = { id: lid, items: [] }; idx.set(lid, g); parLogement.push(g) }
      g.items.push(a)
    }
    for (const g of parLogement) {
      g.count = g.items.length
      g.avgGen = avg(g.items.map(noteGenerale).filter((v) => v != null))
      g.avgMen = avg(g.items.map(noteMenage).filter((v) => v != null))
      g.menageSous = g.avgGen != null && g.avgMen != null && g.avgMen < g.avgGen - 0.001
    }
    return {
      nbLogements,
      avgGen: avg(notes), avgMen: avg(menages),
      sous5MoisCourant, parLogement,
    }
  }, [avis, moisCourant])

  const sous5DuMois = useMemo(() => avis.filter((a) => {
    const n = noteGenerale(a)
    return n != null && n < 5 && String(a.review_date || '').slice(0, 7) === mois
  }), [avis, mois])

  const listeFiltree = useMemo(() => (
    filtreLog === 'tous' ? avis : avis.filter((a) => a.logement_id === filtreLog)
  ), [avis, filtreLog])

  const basculerDeplie = (id) => setDeplies((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })

  // ── LE BANDEAU — trois blocs indicateurs. ──
  const blocs = [
    { cle: 'generale', fond: '#5c8ab8', Ico: Star, compte: fmtFR2(calc.avgGen),
      precision: 'note générale', couleurPrecision: 'rgba(255,255,255,.75)' },
    { cle: 'proprete', fond: palierMenage(calc.avgMen).plein, Ico: Sparkles, compte: fmtFR2(calc.avgMen),
      precision: 'propreté', couleurPrecision: 'rgba(255,255,255,.75)' },
    { cle: 'sous5', fond: AMBRE, Ico: AlertTriangle,
      compte: pret ? `${calc.sous5MoisCourant} avis` : '—',
      precision: 'sous 5★ ce mois', couleurPrecision: '#f5d9a8' },
  ]

  const carteBlanche = { background: '#fff', border: '1px solid #e3e8ee', borderRadius: 12, padding: '12px 13px' }

  return (
    <div data-testid="menage-avis-view" style={{ padding: '0 0 80px', fontFamily: DS.fontBody, color: DS.marineDark }}>
      <CarteTitrePage
        testId="avis-bandeau"
        titre={(
          <span style={{ fontSize: 20 }}>
            Avis{' '}
            <span data-testid="avis-compte" style={{ fontSize: 11.5, fontWeight: 400, fontFamily: DS.fontBody, color: 'rgba(255,255,255,.75)' }}>
              {pret ? `${avis.length} avis · ${calc.nbLogements} logement${calc.nbLogements > 1 ? 's' : ''}` : '—'}
            </span>
          </span>
        )}
        actions={(
          <button type="button" onClick={charger} className="fbz-press" style={boutonCarteTitre()}
            aria-label="Actualiser">
            <RefreshCw size={14} strokeWidth={2.2} />
          </button>
        )}
        sousLeFilet={(
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 10, flexWrap: 'wrap' }}>
            {blocs.map(({ cle, fond, Ico, compte, precision, couleurPrecision }) => (
              <div key={cle} data-testid={`avis-bloc-${cle}`}
                style={{ display: 'flex', alignItems: 'center', gap: 9, minHeight: 44,
                  padding: '6px 12px', borderRadius: 10,
                  background: 'rgba(255,255,255,.13)', fontFamily: DS.fontBody }}>
                <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 9,
                  background: fond, flexShrink: 0, display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center' }}>
                  <Ico size={16} strokeWidth={2.2} color="#fff" />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#fff' }}>{compte}</span>
                  <span style={{ display: 'block', fontSize: 10, marginTop: 1, color: couleurPrecision }}>{precision}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      />

      {loading && <div style={{ padding: 20, color: DS.textMuted, fontSize: 13 }}>Chargement…</div>}
      {error && (
        <div role="alert" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '12px 14px',
          background: DS.dangerBg, color: DS.dangerText, borderRadius: 10, fontSize: 12.5, marginTop: 12 }}>
          <AlertTriangle size={15} strokeWidth={2.2} /> {error}
          <button type="button" onClick={charger} className="fbz-press"
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: DS.dangerText,
              fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', fontSize: 12 }}>
            Réessayer
          </button>
        </div>
      )}
      {!loading && !error && avis.length === 0 && (
        <div style={{ ...carteBlanche, marginTop: 12, textAlign: 'center', color: DS.textMuted, fontSize: 12.5 }}>
          Aucun avis pour l'instant.
        </div>
      )}

      {!loading && !error && avis.length > 0 && (
        <>
          {/* ══ DEUX BLOCS CÔTE À CÔTE — la droite un peu plus large. ══ */}
          <div style={{ display: 'grid', gap: 12, marginTop: 12,
            gridTemplateColumns: estLarge ? '1fr 1.4fr' : '1fr' }}>

            {/* ── PAR LOGEMENT ── */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: DS.textSecondary,
                textTransform: 'uppercase', letterSpacing: 0.5, margin: '2px 0 8px' }}>
                Par logement
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {calc.parLogement.map((g) => (
                  <div key={g.id} data-testid={`avis-logement-${g.id}`}
                    data-menage-sous={g.menageSous ? '1' : '0'}
                    style={g.menageSous
                      ? { ...carteBlanche, background: '#fdfbf6', border: '1px solid #e8d4b0' }
                      : carteBlanche}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#233240' }}>{labelFor(g.id)}</span>
                      <span style={{ fontSize: 11, color: '#98a2ad' }}>· {g.count} avis</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <div data-testid={`avis-note-generale-${g.id}`}
                        style={{ flex: 1, background: '#e8f2fa', borderRadius: 9,
                          padding: '8px 10px', textAlign: 'center' }}>
                        <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#233240',
                          fontVariantNumeric: 'tabular-nums' }}>{fmtFR2(g.avgGen)}</span>
                        <span style={{ display: 'block', fontSize: 9, fontWeight: 700, color: '#6b8bab',
                          textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 1 }}>Général</span>
                      </div>
                      <div data-testid={`avis-note-menage-${g.id}`}
                        style={{ flex: 1, borderRadius: 9, padding: '8px 10px', textAlign: 'center',
                          background: palierMenage(g.avgMen).bg,
                          border: g.menageSous ? '1px solid #e8d4b0' : 'none' }}>
                        <span style={{ display: 'block', fontSize: 14, fontWeight: 700,
                          color: palierMenage(g.avgMen).fg,
                          fontVariantNumeric: 'tabular-nums' }}>{fmtFR2(g.avgMen)}</span>
                        <span style={{ display: 'block', fontSize: 9, fontWeight: 700,
                          color: palierMenage(g.avgMen).fg,
                          textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 1 }}>Ménage</span>
                      </div>
                    </div>
                    {g.menageSous && (
                      <div data-testid={`avis-menage-sous-${g.id}`}
                        style={{ marginTop: 7, fontSize: 11, color: '#8a5f19' }}>
                        Le ménage tire la note vers le bas.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── LES MOINS BONNES NOTES DU MOIS — tous logements mélangés. ── */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 8px' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: DS.textSecondary,
                  textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Les moins bonnes notes <span data-testid="avis-mois" style={{ textTransform: 'none', fontWeight: 400 }}>· {libelleMoisFR(mois)}</span>
                </span>
                <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
                  <button type="button" data-testid="avis-mois-prec" aria-label="Mois précédent"
                    onClick={() => setMois((m) => moisVoisin(m, -1))} className="fbz-press"
                    style={{ width: 28, height: 28, borderRadius: 8, border: `1px solid ${DS.border}`,
                      background: '#fff', color: DS.marine, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>‹</button>
                  <button type="button" data-testid="avis-mois-suiv" aria-label="Mois suivant"
                    onClick={() => setMois((m) => moisVoisin(m, 1))} disabled={mois >= moisCourant}
                    className="fbz-press"
                    style={{ width: 28, height: 28, borderRadius: 8, border: `1px solid ${DS.border}`,
                      background: '#fff', color: DS.marine, fontSize: 13, fontWeight: 700,
                      cursor: mois >= moisCourant ? 'not-allowed' : 'pointer',
                      opacity: mois >= moisCourant ? 0.45 : 1 }}>›</button>
                </span>
              </div>
              <div data-testid="avis-moins-bonnes"
                style={{ background: '#fdf9f7', border: '1px solid #e0b5a5', borderRadius: 12, padding: '12px 13px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: sous5DuMois.length ? 10 : 0 }}>
                  <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: 8,
                    background: '#b8412b', display: 'inline-flex', alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0 }}>
                    <Frown size={15} strokeWidth={2.2} color="#fff" />
                  </span>
                  <span style={{ fontSize: 12, color: '#8f2f1d', fontWeight: 600 }}>
                    {sous5DuMois.length === 0
                      ? `Aucun avis sous 5★ en ${libelleMoisFR(mois)}.`
                      : `${sous5DuMois.length} avis sous 5★ — tout ce qui n'est pas 5 compte contre nous.`}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {sous5DuMois.map((a) => {
                    const men = noteMenage(a)
                    return (
                      <div key={a.id} data-testid={`avis-mauvais-${a.id}`}
                        style={{ background: '#fff', border: '1px solid #f0ddd4', borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                          <PastilleNote n={Number(a.rating)} testId={`avis-mauvais-note-${a.id}`} />
                          <PastilleMenage n={men} testId={`avis-mauvais-menage-${a.id}`} />
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#233240' }}>{labelFor(a.logement_id)}</span>
                          <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#98a2ad' }}>{dateCourteFR(a.review_date)}</span>
                        </div>
                        {/* LE COMMENTAIRE DÉPLIÉ D'OFFICE — c'est là qu'on lit ce qui cloche. */}
                        {a.review_text && (
                          <div style={{ marginTop: 7, fontSize: 12, fontStyle: 'italic', color: '#4a5866',
                            lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {a.review_text}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* ══ TOUS LES AVIS — REPLIÉS par défaut, filtre par logement. ══ */}
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: DS.textSecondary,
              textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              Tous les avis
            </div>
            <div role="tablist" aria-label="Filtrer par logement"
              style={{ display: 'inline-flex', gap: 3, background: '#f6f8fa', borderRadius: 10,
                padding: 3, marginBottom: 10, flexWrap: 'wrap' }}>
              {[{ id: 'tous', nom: 'Tous' }, ...calc.parLogement.map((g) => ({ id: g.id, nom: labelFor(g.id) }))].map((o) => {
                const actif = filtreLog === o.id
                return (
                  <button key={o.id} type="button" role="tab" aria-selected={actif}
                    data-testid={`avis-filtre-${o.id}`}
                    onClick={() => setFiltreLog(o.id)} className="fbz-press"
                    style={{ minHeight: 34, padding: '0 13px', borderRadius: 8, border: 'none',
                      background: actif ? '#fff' : 'transparent',
                      boxShadow: actif ? '0 1px 3px rgba(33,83,135,.15)' : 'none',
                      color: actif ? DS.marineDark : DS.textSecondary,
                      fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
                    {o.nom}
                  </button>
                )
              })}
            </div>
            <div style={{ ...carteBlanche, padding: '4px 13px' }}>
              {listeFiltree.map((a) => {
                const ouvert = deplies.has(a.id)
                const men = noteMenage(a)
                return (
                  <div key={a.id} style={{ borderBottom: '1px solid #f2f4f6' }}>
                    <button type="button" data-testid={`avis-ligne-${a.id}`}
                      onClick={() => basculerDeplie(a.id)} aria-expanded={ouvert}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                        minHeight: 40, background: 'none', border: 'none', padding: '6px 0',
                        cursor: 'pointer', textAlign: 'left', fontFamily: DS.fontBody }}>
                      <span data-testid={`avis-ligne-note-${a.id}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: noteGenerale(a) == null ? '#f1f3f5' : '#e8f2fa',
                          color: noteGenerale(a) == null ? '#98a2ad' : BLEU_GENERALE,
                          borderRadius: 7, padding: '3px 8px', fontSize: 11.5, fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                        <Star size={10} strokeWidth={2.4} /> {fmtFR2(noteGenerale(a))}
                      </span>
                      <PastilleMenage n={men} testId={`avis-ligne-menage-${a.id}`} />
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: '#233240',
                        minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {labelFor(a.logement_id)}
                      </span>
                      <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#98a2ad', flexShrink: 0 }}>
                        {dateCourteFR(a.review_date)}
                      </span>
                      {ouvert
                        ? <ChevronDown size={14} strokeWidth={2.2} color="#98a2ad" style={{ flexShrink: 0 }} />
                        : <ChevronRight size={14} strokeWidth={2.2} color="#98a2ad" style={{ flexShrink: 0 }} />}
                    </button>
                    {ouvert && a.review_text && (
                      <div data-testid={`avis-texte-${a.id}`}
                        style={{ background: '#f8fafc', borderRadius: 8, padding: '9px 11px',
                          margin: '0 0 8px', fontSize: 12, fontStyle: 'italic', color: '#4a5866',
                          lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {a.review_text}
                      </div>
                    )}
                  </div>
                )
              })}
              {listeFiltree.length === 0 && (
                <div style={{ padding: '10px 0', fontSize: 12, color: DS.textMuted }}>
                  Aucun avis pour ce logement.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export const _test = { dateCourteFR, fmtFR2, buildLogLabel, noteMenage, noteGenerale, palierMenage, moisVoisin, libelleMoisFR }
